import type { CharacterAppearance } from '../../core/types';

export interface NewGameResult {
  companyName: string;
  ceoName: string;
  appearance: CharacterAppearance;
  bonusStats: Record<string, number>;
  difficulty: 'easy' | 'normal' | 'hard';
}

/**
 * New Game screen — company name, CEO name, appearance customization, skill points.
 * Returns the result via a callback when the player clicks Start.
 */
export function showNewGameScreen(
  parent: HTMLElement,
  onStart: (result: NewGameResult) => void,
  onLoad?: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'game-ui';
  overlay.style.cssText = 'position:fixed;inset:0;background:var(--color-bg-primary);z-index:300;display:flex;flex-direction:column;align-items:center;justify-content:center;';

  const container = document.createElement('div');
  container.style.cssText = 'width:400px;max-width:90vw;display:flex;flex-direction:column;gap:16px;';

  // Title
  const title = document.createElement('h1');
  title.textContent = 'Office Tycoon';
  title.style.cssText = 'text-align:center;font-size:28px;color:var(--color-accent);margin-bottom:8px;';
  container.appendChild(title);

  const subtitle = document.createElement('div');
  subtitle.textContent = 'Start a New Game';
  subtitle.style.cssText = 'text-align:center;font-size:14px;color:var(--color-text-secondary);margin-bottom:16px;';
  container.appendChild(subtitle);

  // Company name
  const companyInput = createInput('Studio Name', 'Pixel Dreams');
  container.appendChild(companyInput.row);

  // CEO name
  const ceoInput = createInput('Your Name', 'Alex Chen');
  container.appendChild(ceoInput.row);

  // Appearance
  const appearanceSection = document.createElement('div');
  appearanceSection.style.cssText = 'background:var(--color-bg-surface);border:1px solid var(--border-color);border-radius:4px;padding:12px;';

  const appTitle = document.createElement('div');
  appTitle.textContent = 'Appearance';
  appTitle.style.cssText = 'font-size:12px;color:var(--color-text-secondary);margin-bottom:8px;';
  appearanceSection.appendChild(appTitle);

  const appearance: CharacterAppearance = {
    bodyType: 0, skinTone: 2, hairStyle: 1, hairColor: 3,
    outfit: 0, outfitColor: 0, accessory: 0,
  };

  // Character preview
  const previewRow = document.createElement('div');
  previewRow.style.cssText = 'display:flex;gap:16px;align-items:center;margin-bottom:8px;';

  const previewCanvas = document.createElement('canvas');
  previewCanvas.width = 80;
  previewCanvas.height = 80;
  previewCanvas.style.cssText = 'border:1px solid var(--border-color);border-radius:8px;background:var(--color-bg-primary);flex-shrink:0;';
  previewRow.appendChild(previewCanvas);

  function updatePreview() {
    const ctx = previewCanvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, 80, 80);
    drawPortrait2D(ctx, 40, 52, appearance);
  }

  const appControls = document.createElement('div');
  appControls.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;flex:1;align-content:flex-start;';
  previewRow.appendChild(appControls);
  appearanceSection.appendChild(previewRow);

  const skinNames = ['Fair', 'Light', 'Peach', 'Tan', 'Bronze', 'Brown', 'Dark', 'Deep'];
  const hairNames = ['Crew Cut', 'Short', 'Medium', 'Long', 'Buzz', 'Wavy', 'Curly', 'Mohawk', 'Ponytail', 'Braids', 'Afro', 'Spiky', 'Side Part', 'Slicked', 'Messy', 'Bald'];
  const hairColorNames = ['Black', 'Dark Brown', 'Brown', 'Light Brown', 'Blonde', 'Grey', 'White', 'Red', 'Blue', 'Pink'];
  const outfitNames = ['Blue', 'Red', 'Green', 'Gold', 'Purple', 'Orange', 'Teal', 'Pink', 'Charcoal', 'Silver', 'Cream', 'Black'];
  const accessoryNames = ['None', 'Glasses', 'Shades', 'Round Glasses', 'Headband', 'Earring', 'Scarf', 'Cap'];

  const appFields: { key: keyof CharacterAppearance; label: string; max: number; names: string[] }[] = [
    { key: 'skinTone', label: 'Skin', max: 7, names: skinNames },
    { key: 'hairStyle', label: 'Hair', max: 15, names: hairNames },
    { key: 'hairColor', label: 'Color', max: 9, names: hairColorNames },
    { key: 'outfitColor', label: 'Outfit', max: 11, names: outfitNames },
    { key: 'accessory', label: 'Accessory', max: 7, names: accessoryNames },
  ];

  for (const field of appFields) {
    const btn = document.createElement('button');
    btn.className = 'btn';
    const getName = () => field.names[appearance[field.key]] ?? String(appearance[field.key]);
    btn.textContent = `${field.label}: ${getName()}`;
    btn.style.cssText = 'font-size:10px;min-width:80px;';
    btn.addEventListener('click', () => {
      appearance[field.key] = (appearance[field.key] + 1) % (field.max + 1);
      btn.textContent = `${field.label}: ${getName()}`;
      updatePreview();
    });
    appControls.appendChild(btn);
  }
  updatePreview(); // Initial render
  container.appendChild(appearanceSection);

  // Skill points
  const skillSection = document.createElement('div');
  skillSection.style.cssText = 'background:var(--color-bg-surface);border:1px solid var(--border-color);border-radius:4px;padding:12px;';

  const skillTitle = document.createElement('div');
  skillTitle.textContent = 'Distribute 10 Skill Points';
  skillTitle.style.cssText = 'font-size:12px;color:var(--color-text-secondary);margin-bottom:8px;';
  skillSection.appendChild(skillTitle);

  const stats: Record<string, number> = { coding: 0, art: 0, sound: 0, design: 0 };
  let remaining = 10;

  const remainingEl = document.createElement('div');
  remainingEl.style.cssText = 'font-size:11px;color:var(--color-xp);text-align:center;margin-bottom:8px;';

  const updateRemaining = () => {
    remaining = 10 - Object.values(stats).reduce((a, b) => a + b, 0);
    remainingEl.textContent = `Points remaining: ${remaining}`;
  };
  updateRemaining();
  skillSection.appendChild(remainingEl);

  const statColors: Record<string, string> = {
    coding: 'var(--color-stat-coding)',
    art: 'var(--color-stat-art)',
    sound: 'var(--color-stat-sound)',
    design: 'var(--color-stat-design)',
  };

  const skillGrid = document.createElement('div');
  skillGrid.style.cssText = 'display:grid;grid-template-columns:70px 24px 1fr 28px 28px;gap:4px;align-items:center;';

  for (const stat of Object.keys(stats)) {
    const label = document.createElement('span');
    label.textContent = stat.charAt(0).toUpperCase() + stat.slice(1);
    label.style.cssText = `font-size:12px;color:${statColors[stat]};`;

    const bar = document.createElement('div');
    bar.className = 'progress-bar';
    bar.style.height = '8px';
    const fill = document.createElement('div');
    fill.className = 'progress-bar__fill';
    fill.style.cssText = `width:0%;background:${statColors[stat]};`;
    bar.appendChild(fill);

    const minusBtn = document.createElement('button');
    minusBtn.className = 'btn btn--icon';
    minusBtn.textContent = '-';
    minusBtn.style.cssText = 'font-size:14px;padding:0;min-width:24px;min-height:24px;';

    const plusBtn = document.createElement('button');
    plusBtn.className = 'btn btn--icon';
    plusBtn.textContent = '+';
    plusBtn.style.cssText = 'font-size:14px;padding:0;min-width:24px;min-height:24px;';

    const valEl = document.createElement('span');
    valEl.style.cssText = `font-size:12px;font-weight:600;text-align:center;color:${statColors[stat]};`;
    valEl.textContent = '0';

    const updateBar = () => {
      fill.style.width = `${(stats[stat]! / 10) * 100}%`;
      valEl.textContent = String(stats[stat]);
      minusBtn.disabled = stats[stat]! <= 0;
      plusBtn.disabled = remaining <= 0;
    };

    minusBtn.addEventListener('click', () => {
      if (stats[stat]! > 0) { stats[stat]!--; updateRemaining(); updateBar(); }
    });
    plusBtn.addEventListener('click', () => {
      if (remaining > 0) { stats[stat]!++; updateRemaining(); updateBar(); }
    });

    updateBar();

    skillGrid.appendChild(label);
    skillGrid.appendChild(valEl);
    skillGrid.appendChild(bar);
    skillGrid.appendChild(minusBtn);
    skillGrid.appendChild(plusBtn);
  }
  skillSection.appendChild(skillGrid);
  container.appendChild(skillSection);

  // Difficulty
  const diffSection = document.createElement('div');
  diffSection.style.cssText = 'display:flex;flex-direction:column;gap:4px;';
  const diffLabel = document.createElement('div');
  diffLabel.textContent = 'Difficulty';
  diffLabel.style.cssText = 'font-size:11px;color:var(--color-text-secondary);';
  diffSection.appendChild(diffLabel);

  const diffRow = document.createElement('div');
  diffRow.style.cssText = 'display:flex;gap:4px;';
  let selectedDifficulty: 'easy' | 'normal' | 'hard' = 'normal';
  const diffOptions: { id: 'easy' | 'normal' | 'hard'; label: string; desc: string; color: string }[] = [
    { id: 'easy', label: 'Easy', desc: '+50% starting funds, slower morale drain', color: 'var(--color-success)' },
    { id: 'normal', label: 'Normal', desc: 'The intended experience', color: 'var(--color-text-primary)' },
    { id: 'hard', label: 'Hard', desc: 'Less funds, harsher reviews, faster morale drain', color: 'var(--color-danger)' },
  ];
  const diffDesc = document.createElement('div');
  diffDesc.style.cssText = 'font-size:10px;color:var(--color-text-muted);min-height:14px;';
  diffDesc.textContent = diffOptions[1]!.desc;

  for (const opt of diffOptions) {
    const btn = document.createElement('button');
    btn.className = `btn${opt.id === 'normal' ? ' btn--active' : ''}`;
    btn.textContent = opt.label;
    btn.style.cssText = `flex:1;font-size:12px;padding:6px;color:${opt.color};`;
    btn.addEventListener('click', () => {
      selectedDifficulty = opt.id;
      diffDesc.textContent = opt.desc;
      for (const b of diffRow.querySelectorAll('.btn')) b.className = 'btn';
      btn.className = 'btn btn--active';
    });
    diffRow.appendChild(btn);
  }
  diffSection.appendChild(diffRow);
  diffSection.appendChild(diffDesc);
  container.appendChild(diffSection);

  // Start button
  const startBtn = document.createElement('button');
  startBtn.className = 'btn btn--primary';
  startBtn.textContent = 'Start Game';
  startBtn.style.cssText = 'padding:12px;font-size:16px;margin-top:8px;';
  startBtn.addEventListener('click', () => {
    const companyName = companyInput.input.value.trim() || 'Pixel Dreams';
    const ceoName = ceoInput.input.value.trim() || 'Alex Chen';

    overlay.remove();
    onStart({
      companyName,
      ceoName,
      appearance,
      bonusStats: { ...stats },
      difficulty: selectedDifficulty,
    });
  });
  container.appendChild(startBtn);

  // Load Game button
  if (onLoad) {
    const loadBtn = document.createElement('button');
    loadBtn.className = 'btn';
    loadBtn.textContent = 'Load Game';
    loadBtn.style.cssText = 'padding:10px;font-size:14px;';
    loadBtn.addEventListener('click', () => {
      overlay.remove();
      onLoad();
    });
    container.appendChild(loadBtn);
  }

  overlay.appendChild(container);
  parent.appendChild(overlay);

  // Focus company name
  companyInput.input.select();
}

function createInput(label: string, defaultValue: string): { row: HTMLDivElement; input: HTMLInputElement } {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;flex-direction:column;gap:4px;';

  const lbl = document.createElement('label');
  lbl.textContent = label;
  lbl.style.cssText = 'font-size:11px;color:var(--color-text-secondary);';
  row.appendChild(lbl);

  const input = document.createElement('input');
  input.type = 'text';
  input.value = defaultValue;
  input.maxLength = 20;
  input.style.cssText = 'padding:8px 12px;background:var(--color-bg-surface);border:1px solid var(--border-color);border-radius:4px;color:var(--color-text-primary);font-family:var(--font-ui);font-size:14px;';
  row.appendChild(input);

  return { row, input };
}

function drawPortrait2D(ctx: CanvasRenderingContext2D, cx: number, cy: number, app: CharacterAppearance): void {
  const skinColors = ['#ffe0bd', '#f5d0b0', '#ebc8a0', '#d4a878', '#c09060', '#9a7050', '#7a5838', '#5a3820'];
  const outfitColors = ['#3868a8', '#a03838', '#388838', '#a88838', '#6838a8', '#a85838', '#38a8a8', '#a83888', '#484858', '#b0b0b8', '#d0c8b8', '#303038'];
  const hairColors = ['#302010', '#503820', '#785838', '#a88848', '#c8b060', '#b0b0b0', '#e0d8d0', '#a82828', '#2870b0', '#aa33aa'];

  const skin = skinColors[app.skinTone % skinColors.length]!;
  const outfit = outfitColors[app.outfitColor % outfitColors.length]!;
  const hair = hairColors[app.hairColor % hairColors.length]!;

  // Body
  ctx.fillStyle = outfit;
  ctx.beginPath();
  ctx.roundRect(cx - 10, cy - 4, 20, 14, 3);
  ctx.fill();

  // Neck
  ctx.fillStyle = skin;
  ctx.fillRect(cx - 3, cy - 8, 6, 6);

  // Head
  ctx.beginPath();
  ctx.arc(cx, cy - 14, 10, 0, Math.PI * 2);
  ctx.fillStyle = skin;
  ctx.fill();

  // Face highlight
  ctx.beginPath();
  ctx.arc(cx - 2, cy - 15, 7, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.15)';
  ctx.fill();

  // Eyes
  ctx.fillStyle = '#202030';
  ctx.beginPath();
  ctx.arc(cx - 4, cy - 15, 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx + 4, cy - 15, 1.5, 0, Math.PI * 2);
  ctx.fill();

  // Mouth
  ctx.beginPath();
  ctx.arc(cx, cy - 10, 2, 0, Math.PI);
  ctx.strokeStyle = skin;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Hair
  ctx.fillStyle = hair;
  const hs = app.hairStyle % 4;
  if (hs === 0) {
    ctx.beginPath();
    ctx.arc(cx, cy - 14, 10.5, Math.PI, 0);
    ctx.fill();
  } else if (hs === 1) {
    ctx.beginPath();
    ctx.arc(cx, cy - 14, 11, Math.PI * 1.1, -0.1 * Math.PI);
    ctx.fill();
  } else if (hs === 2) {
    ctx.beginPath();
    ctx.arc(cx, cy - 14, 11, Math.PI * 1.2, -0.2 * Math.PI);
    ctx.fill();
    ctx.fillRect(cx - 11, cy - 18, 4, 14);
    ctx.fillRect(cx + 7, cy - 18, 4, 14);
  }
  // hs === 3 = bald

  // Glasses
  if (app.accessory > 0 && app.accessory <= 3) {
    ctx.strokeStyle = '#404060';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx - 4.5, cy - 15, 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx + 4.5, cy - 15, 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - 1.5, cy - 15);
    ctx.lineTo(cx + 1.5, cy - 15);
    ctx.stroke();
  }
}
